"use client";

import { ProductData } from "@/lib/5874/types";
import {
	CarouselTypeFriendlyName,
	getProductCarouselItems,
} from "@/lib/bigcommerce/product";
import { Builder } from "@builder.io/react";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import ProductRecommendationCarousel, {
	ProductRecommendationCarouselProps,
} from "../ProductRecommendationCarousel/ProductRecommendationCarousel";
import RecentlyViewedCarousel from "../RecentlyViewedCarousel/RecentlyViewedCarousel";

interface ProductCarouselProps
	extends Omit<ProductRecommendationCarouselProps, "products" | "isLoading"> {
	algoliaSettings: {
		algoliaBackupQuery?: string;
		algoliaGroup?: CarouselTypeFriendlyName;
		productBrand?: string;
	};
	preventCLS?: boolean;
	queryId?: string;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({
	title,
	useProductCards,
	graphqlId,
	placementProductId,
	algoliaSettings,
	preventCLS,
	queryId,
	...props
}) => {
	const [products, setProducts] = useState<Array<ProductData> | undefined>([]);
	const [isLoading, setIsLoading] = useState(true);

	const carouselType = useMemo(() => {
		let type = algoliaSettings?.algoliaGroup || undefined;

		if (!type && (algoliaSettings?.algoliaBackupQuery || queryId)) {
			type = "Custom Query";
		}

		if (!type && !algoliaSettings?.algoliaBackupQuery) {
			type = undefined;
		}

		return type;
	}, [algoliaSettings]);

	if (carouselType === "Recently Viewed") {
		return <RecentlyViewedCarousel id={Number(graphqlId)} />;
	}

	useEffect(() => {
		// if there is no type
		if (!carouselType || carouselType === "Hidden") {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		const runRecommendations = async () => {
			try {
				const products = await getProductCarouselItems({
					productId: graphqlId || queryId,
					carouselType: carouselType,
					queryId: queryId,
					backupQuery: algoliaSettings?.algoliaBackupQuery,
					productBrand: algoliaSettings?.productBrand
				});

				setProducts(products || []);
			} catch (e) {
				console.error(`❌ API error:`, e);
				setProducts([]);
			} finally {
				setIsLoading(false);
			}
		};

		runRecommendations();
	}, [carouselType, graphqlId, queryId]);

	if (carouselType === "Hidden") {
		return null;
	}

	if (!isLoading && !products?.length) {
		return null;
	}

	// check if the component is being used from builder
	const isFromBuilder = (props as any).builderBlock !== undefined;

	const getEventName = () => {
		const type = carouselType || "Product Carousel";

		return `${type} Products Viewed`;
	};

	const carouselProps: ProductRecommendationCarouselProps = {
		title,
		useProductCards: isFromBuilder ? true : useProductCards,
		placementProductId,
		graphqlId,
		products,
		isLoading,
		showAllProducts: true,

		displayOptions: {
			displayType:
				isFromBuilder || useProductCards ? "cards" : "recommendations",
			showAvailableProductsOnly: true,
			showProductForm: false,
		},
	};

	const carousel = (
		<ProductRecommendationCarousel
			{...carouselProps}
			data-eventname={getEventName()}
		/>
	);

	return (
		<>
			{preventCLS ? carousel : <Suspense fallback={null}>{carousel}</Suspense>}
		</>
	);
};

Builder.registerComponent(ProductCarousel, {
	name: "Product Carousel",
	inputs: [
		{
			name: "title",
			friendlyName: "Title",
			type: "longText",
			defaultValue: "Title",
		},
		{
			name: "queryId",
			friendlyName: "Algolia Search Query",
			type: "string",
		},
	],
});

export default ProductCarousel;
